import { getStoredUser, refreshTokens } from './auth';

/**
 * fetch() with one retry: on any 401, refresh the access token via the stored
 * refresh token and retry once with the new one.
 *
 * This deliberately triggers on the status alone rather than on signus-service's
 * "JWT ..." error message. Matching the message meant a 401 phrased any other way
 * (or with an empty body) skipped the refresh entirely, so a merely-expired token
 * surfaced to the user as a hard failure. A refresh on a 401 that wasn't about
 * expiry just costs one extra request; missing a real expiry costs the session.
 *
 * When the refresh itself fails, `refreshTokens` clears the session and broadcasts
 * via `onSessionEnded` — App drops to the signed-out screen rather than leaving the
 * pane rendering a signed-in UI that can no longer do anything.
 *
 * `buildInit` is called once per attempt so the Authorization header always
 * carries the token for that attempt. The response body is never consumed here —
 * callers still call `.json()`/`.blob()` on the returned Response exactly as before.
 */
export async function fetchWithTokenRefresh(
  url: string,
  accessToken: string,
  buildInit: (accessToken: string) => RequestInit,
): Promise<Response> {
  const attempt = (token: string): Promise<Response> => fetch(url, buildInit(token));

  let res = await attempt(accessToken);

  if (res.status === 401) {
    const user = getStoredUser();
    if (user) {
      const refreshed = await refreshTokens(user);
      if (refreshed) {
        res = await attempt(refreshed.accessToken);
      }
    }
  }

  return res;
}

export function extractErrorMessage(json: unknown, fallback: string): string {
  const payload = json as {
    errors?: { errorMessage?: string }[];
    errorMessage?: string;
    message?: string;
  } | null;
  return (
    payload?.errors?.[0]?.errorMessage ?? payload?.errorMessage ?? payload?.message ?? fallback
  );
}

export function extractErrorType(json: unknown): string | undefined {
  const payload = json as { errors?: { type?: string }[] } | null;
  return payload?.errors?.[0]?.type;
}

/** An API failure that carries signus-service's `SignusServiceApiErrorType` value, if any. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly errorType?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
